<template>
  <TransitionRoot appear :show="alert.state.visible" :key="alert.state.id" as="template">
    <Dialog as="div" class="relative z-9999" :static="true">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/40" />
      </TransitionChild>

      <div class="fixed inset-0 flex items-center justify-center p-4">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel
            class="w-full max-w-sm transform overflow-hidden rounded-2xl bg-white p-6 text-center shadow-xl transition-all"
          >
            <template v-if="alert.state.message">
              <div class="flex flex-row gap-2">
                <!-- Icon -->
                <div class="flex justify-center mb-3">
                  <component :is="icon" :class="['w-10 h-10', colorClass]" />
                </div>

                <div class="w-full">
                  <DialogTitle class="text-start text-lg font-medium mt-2 text-gray-900 mon">{{
                    alert.state.title
                  }}</DialogTitle>
                  <DialogDescription
                    class="text-start text-gray-600 leading-4 whitespace-pre-line"
                    >{{ alert.state.message }}</DialogDescription
                  >
                </div>
              </div>
            </template>

            <template v-else>
              <div class="flex flex-row gap-2 items-center justify-center">
                <!-- Icon -->
                <div class="flex justify-center">
                  <component :is="icon" :class="['w-10 h-10', colorClass]" />
                </div>

                <div class="w-full">
                  <DialogTitle
                    class="text-start text-lg font-medium leading-none text-gray-900 mon"
                    >{{ alert.state.title }}</DialogTitle
                  >
                </div>
              </div>
            </template>

            <div class="mt-6 flex justify-end gap-3">
              <button
                v-if="alert.state.cancelText"
                type="button"
                @click="alert.cancel"
                class="rounded-md border border-gray-100 py-1 w-15 text-sm font-medium hover:bg-gray-100"
              >
                {{ alert.state.cancelText }}
              </button>
              <button
                type="button"
                @click="alert.confirm"
                :class="['rounded-md py-1 w-15 text-sm font-medium text-white', buttonClass]"
              >
                {{ alert.state.confirmText }}
              </button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { useAlert } from '@/core/alert'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogDescription,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import {
  IconCircleCheck,
  IconCircleX,
  IconExclamationCircle,
  IconInfoCircle,
} from '@tabler/icons-vue'
import { computed } from 'vue'

const alert = useAlert()

const icon = computed(() => {
  switch (alert.state.type) {
    case 'success':
      return IconCircleCheck
    case 'error':
      return IconCircleX
    case 'warning':
      return IconExclamationCircle
    default:
      return IconInfoCircle
  }
})

const colorClass = computed(() => {
  switch (alert.state.type) {
    case 'success':
      return 'text-green-500'
    case 'error':
      return 'text-red-500'
    case 'warning':
      return 'text-yellow-500'
    default:
      return 'text-blue-500'
  }
})

const buttonClass = computed(() => {
  switch (alert.state.type) {
    case 'success':
      return 'bg-green-600 hover:bg-green-700'
    case 'error':
      return 'bg-red-600 hover:bg-red-700'
    case 'warning':
      return 'bg-yellow-500 hover:bg-yellow-600'
    default:
      return 'bg-blue-600 hover:bg-blue-700'
  }
})
</script>
